import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalBody,
  Button,
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';
import metaMask from '../../../assets/images/nft/newrecord.png';

const PARTICIPATION = {
  PRESENT: false,
  DELEGATE: true,
};

const PARTICIPATION_LABEL = {
  [PARTICIPATION.PRESENT]: 'Present',
  [PARTICIPATION.DELEGATE]: 'Delegate',
};

const isValidEmail = value =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

// Confirm popup for Invitation Evaluation "Save & Confirm".
//  - Option box: Present / Delegate.
//  - When Delegate is chosen, a Participant Information card appears with
//    Name and Email (email-format validated).
//  - Submit -> onConfirm({ mode, name, email }).
//  - Cancel -> onClose; nothing is saved.
const ConfirmInvitationModal = ({ show, submitting, onConfirm, onClose }) => {
  const [mode, setMode] = useState(PARTICIPATION.PRESENT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (show) {
      setMode(PARTICIPATION.PRESENT);
      setName('');
      setEmail('');
      setJobTitle('');
      setTouched(false);
    }
  }, [show]);

  const isDelegate = mode === PARTICIPATION.DELEGATE;
  const nameInvalid = isDelegate && !name.trim();
  const emailInvalid = isDelegate && !isValidEmail(email);
  const jobTitleInvalid = isDelegate && !jobTitle.trim();

  const handleSubmit = () => {
    if (isDelegate && (nameInvalid || emailInvalid || jobTitleInvalid)) {
      setTouched(true);
      return;
    }
    onConfirm({
      mode,
      name: name.trim(),
      email: email.trim(),
      jobTitle: jobTitle.trim(),
    });
  };

  return (
    <Modal isOpen={show} toggle={onClose} centered data-bs-backdrop='static'>
      <ModalBody className='p-4 text-center'>
        <img src={metaMask} alt='' height='65' className='mb-2 pb-2' />
        <h5 className='fw-semibold mb-2'>Confirm Invitation</h5>
        <p className='text-muted fs-13 mb-3'>
          Please select your arrival below to continue confirming the
          invitation.
        </p>

        <div className='d-flex gap-2 mb-3 text-start'>
          {[PARTICIPATION.PRESENT, PARTICIPATION.DELEGATE].map(opt => (
            <div
              key={String(opt)}
              className='form-check card-radio flex-grow-1'
            >
              <Input
                id={`invitation-participation-${PARTICIPATION_LABEL[
                  opt
                ].toLowerCase()}`}
                name='invitation-participation-mode'
                type='radio'
                className='form-check-input'
                checked={mode === opt}
                onChange={() => setMode(opt)}
              />
              <Label
                className='form-check-label d-flex align-items-center gap-2'
                for={`invitation-participation-${PARTICIPATION_LABEL[
                  opt
                ].toLowerCase()}`}
              >
                <i
                  className={`${
                    opt === PARTICIPATION.PRESENT
                      ? 'ri-user-line'
                      : 'ri-user-shared-line'
                  } fs-16 align-bottom`}
                ></i>
                <span className='fw-medium'>{PARTICIPATION_LABEL[opt]}</span>
              </Label>
            </div>
          ))}
        </div>

        {isDelegate && (
          <Card className='border shadow-none mb-3 text-start'>
            <CardBody>
              <h6 className='fw-semibold mb-1'>Participant Information</h6>
              <p className='text-muted fs-13 mb-3'>
                Enter the information of the participant to be delegated
              </p>
              <div className='mb-3'>
                <Label className='form-label'>Name</Label>
                <Input
                  type='text'
                  placeholder='Enter name'
                  value={name}
                  invalid={touched && nameInvalid}
                  onChange={e => setName(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
                <FormFeedback>Name is required</FormFeedback>
              </div>
              <div className='mb-3'>
                <Label className='form-label'>Email</Label>
                <Input
                  type='text'
                  placeholder='Enter email'
                  value={email}
                  invalid={touched && emailInvalid}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
                <FormFeedback>Enter a valid email address</FormFeedback>
              </div>
              <div className='mb-1'>
                <Label className='form-label'>Job Title</Label>
                <Input
                  type='text'
                  placeholder='Enter job title'
                  value={jobTitle}
                  invalid={touched && jobTitleInvalid}
                  onChange={e => setJobTitle(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
                <FormFeedback>Job title is required</FormFeedback>
              </div>
            </CardBody>
          </Card>
        )}

        <div className='d-flex align-items-center justify-content-between'>
          <Button
            color='link'
            className='text-decoration-none p-0'
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            className='px-4'
            onClick={handleSubmit}
            disabled={submitting}
          >
            Submit
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ConfirmInvitationModal;
